import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, ShieldAlert, Truck, Bike, Car, Upload,
  FileText, CheckCircle2, X, Loader2, Eye,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DOC_COUNTRIES, platePlaceholder, licensePlaceholder } from "@/lib/doc_country";

const VEHICLE_OPTIONS = [
  { id: "moto", label: "Moto", icon: Bike, desc: "Documenti, piccoli pacchi" },
  { id: "scooter", label: "Scooter", icon: Bike, desc: "Pacchi piccoli, agile in città" },
  { id: "auto", label: "Auto", icon: Car, desc: "Pacchi medi, fino a 10 kg" },
  { id: "auto_grande", label: "Auto grande / SUV", icon: Car, desc: "Pacchi Large, fino a 19 kg" },
  { id: "furgone", label: "Furgone", icon: Truck, desc: "Tutto inclusi mobili e merce voluminosa" },
];

const DOC_STATUS_LABEL = {
  missing: { label: "Documenti mancanti", tone: "red" },
  incomplete: { label: "Documenti incompleti", tone: "amber" },
  pending: { label: "In verifica", tone: "amber" },
  verified_basic: { label: "Verificato base", tone: "emerald" },
  verified_full: { label: "Verificato completo", tone: "emerald" },
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function DriverVehicle() {
  const { user, refresh } = useAuth();
  const info = user?.driver_info || {};

  const [form, setForm] = useState({
    vehicle_type: info.vehicle_type || "",
    vehicle_brand: info.vehicle_brand || "",
    vehicle_model: info.vehicle_model || "",
    vehicle_color: info.vehicle_color || "",
    vehicle_plate: info.vehicle_plate || "",
    license_number: info.license_number || "",
    vehicle_capacity_notes: info.vehicle_capacity_notes || "",
    doc_country: info.doc_country || "CH",
    // Phase 43.12 — compliance
    license_expiry: info.license_expiry || "",
    insurance_company: info.insurance_company || "",
    insurance_expiry: info.insurance_expiry || "",
  });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  // Documents state
  const [docState, setDocState] = useState({
    types: [],
    documents: {},
    status: "missing",
    missing_required: [],
    max_bytes: 6 * 1024 * 1024,
  });
  const [docsLoading, setDocsLoading] = useState(true);
  const [uploading, setUploading] = useState({}); // { [type]: true }
  const fileRefs = useRef({});

  // Phase 44.31.6 — Anchor scroll: se vieni da Profilo → "Documenti", atterri qui
  // ma la pagina ha 2 section (Veicolo, Documenti). Quando hash="#documents"
  // facciamo scroll + flash highlight per chiarire visivamente la separazione.
  const location = useLocation();
  const documentsRef = useRef(null);
  const [docFlash, setDocFlash] = useState(false);
  useEffect(() => {
    if (location.hash !== "#documents") return;
    const t = setTimeout(() => {
      const el = documentsRef.current;
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setDocFlash(true);
        setTimeout(() => setDocFlash(false), 1800);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [location.hash, docsLoading]);

  const loadDocs = async () => {
    setDocsLoading(true);
    try {
      const { data } = await api.get("/driver/documents");
      setDocState(data);
    } catch (e) {
      // silent — driver may not yet have docs collection
    } finally {
      setDocsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.driver_info) {
      const i = user.driver_info;
      setForm({
        vehicle_type: i.vehicle_type || "",
        vehicle_brand: i.vehicle_brand || "",
        vehicle_model: i.vehicle_model || "",
        vehicle_color: i.vehicle_color || "",
        vehicle_plate: i.vehicle_plate || "",
        license_number: i.license_number || "",
        vehicle_capacity_notes: i.vehicle_capacity_notes || "",
        doc_country: i.doc_country || "CH",
        license_expiry: i.license_expiry || "",
        insurance_company: i.insurance_company || "",
        insurance_expiry: i.insurance_expiry || "",
      });
    }
  }, [user]);

  useEffect(() => {
    loadDocs();
  }, []);

  const set = (k) => (e) => {
    setForm((s) => ({ ...s, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.vehicle_type) er.vehicle_type = "Seleziona un tipo di veicolo";
    if (!form.vehicle_brand.trim()) er.vehicle_brand = "Marca obbligatoria";
    if (!form.vehicle_model.trim()) er.vehicle_model = "Modello obbligatorio";
    if (!form.vehicle_color.trim()) er.vehicle_color = "Colore obbligatorio";
    if (!form.vehicle_plate.trim()) er.vehicle_plate = "Targa obbligatoria";
    if (!form.license_number.trim()) er.license_number = "Numero patente obbligatorio";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const save = async () => {
    if (!validate()) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setBusy(true);
    try {
      await api.put("/driver/vehicle", form);
      toast.success("Veicolo aggiornato");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const docTypes = useMemo(() => docState.types || [], [docState.types]);

  const onPickFile = (type) => () => {
    fileRefs.current[type]?.click();
  };

  const onFileChange = (type) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset
    if (!file) return;
    if (file.size > docState.max_bytes) {
      toast.error(`File troppo grande (max ${(docState.max_bytes / (1024 * 1024)).toFixed(0)} MB)`);
      return;
    }
    setUploading((u) => ({ ...u, [type]: true }));
    try {
      const data_base64 = await fileToBase64(file);
      await api.post("/driver/documents", {
        type,
        filename: file.name,
        mime: file.type || "application/octet-stream",
        data_base64,
      });
      toast.success("Documento caricato");
      await loadDocs();
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading((u) => ({ ...u, [type]: false }));
    }
  };

  const removeDoc = async (type) => {
    if (!window.confirm("Eliminare il documento caricato?")) return;
    try {
      await api.delete(`/driver/documents/${type}`);
      toast.success("Documento rimosso");
      await loadDocs();
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const viewDoc = async (type) => {
    try {
      const { data } = await api.get(`/driver/documents/${type}/file`);
      const b64 = data.data_base64;
      const url = b64.startsWith("data:") ? b64 : `data:${data.mime};base64,${b64}`;
      const w = window.open();
      if (!w) {
        toast.error("Disabilita il blocco popup per visualizzare il documento");
        return;
      }
      if (data.mime?.startsWith("image/")) {
        w.document.write(`<img src="${url}" style="max-width:100%;height:auto;display:block;margin:auto"/>`);
      } else {
        w.location.href = url;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="driver-vehicle-page">
      <Link to="/driver/profile" className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-muted hover:text-dapnow-ink">
        <ArrowLeft className="h-4 w-4" /> Profilo
      </Link>
      <div>
        <p className="overline">Driver · Veicolo</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">Il tuo veicolo</h1>
        <p className="text-dapnow-muted text-sm mt-2">
          Tipo veicolo, marca, targa, patente e scadenze di patente + assicurazione.
          I documenti (upload patente, libretto, assicurazione) si gestiscono nella pagina dedicata{" "}
          <Link to="/driver/documents" className="text-dapnow-blue font-semibold hover:underline">Documenti →</Link>
        </p>
      </div>

      <section className="card-soft p-5 sm:p-6 space-y-5" data-testid="vehicle-form-section">
        <p className="overline">Tipo di veicolo *</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {VEHICLE_OPTIONS.map((o) => {
            const active = form.vehicle_type === o.id;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  setForm((s) => ({ ...s, vehicle_type: o.id }));
                  setErrors((er) => ({ ...er, vehicle_type: undefined }));
                }}
                data-testid={`vehicle-option-${o.id}`}
                className={`text-left p-4 rounded-2xl border transition ${active ? "border-dapnow-blue bg-dapnow-blue-soft" : "border-dapnow-line bg-white hover:border-dapnow-blue/40"}`}
              >
                <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${active ? "bg-dapnow-blue text-white" : "bg-dapnow-bg text-dapnow-ink"}`}>
                  <o.icon className="h-5 w-5" />
                </span>
                <p className="mt-3 font-display text-base font-semibold tracking-tight">{o.label}</p>
                <p className="text-xs text-dapnow-muted">{o.desc}</p>
              </button>
            );
          })}
        </div>
        {errors.vehicle_type && <p className="text-xs text-dapnow-red font-medium">{errors.vehicle_type}</p>}

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Marca *" required testid="vehicle-brand" v={form.vehicle_brand} on={set("vehicle_brand")} placeholder="Toyota" error={errors.vehicle_brand} />
          <Field label="Modello *" required testid="vehicle-model" v={form.vehicle_model} on={set("vehicle_model")} placeholder="Yaris" error={errors.vehicle_model} />
          <Field label="Colore *" required testid="vehicle-color" v={form.vehicle_color} on={set("vehicle_color")} placeholder="Bianco" error={errors.vehicle_color} />
          <div>
            <label className="text-sm font-semibold tracking-tight">Paese targa/patente</label>
            <select
              data-testid="vehicle-doc-country"
              value={form.doc_country}
              onChange={set("doc_country")}
              className="input-tall mt-1.5 w-full"
            >
              {DOC_COUNTRIES.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
          <Field label="Targa *" required testid="vehicle-plate" v={form.vehicle_plate} on={set("vehicle_plate")} placeholder={platePlaceholder(form.doc_country)} error={errors.vehicle_plate} />
          <Field label="Numero patente *" required testid="vehicle-license" v={form.license_number} on={set("license_number")} placeholder={licensePlaceholder(form.doc_country)} error={errors.license_number} />
          <div className="sm:col-span-2">
            <Field label="Note capacità (opz.)" testid="vehicle-capacity" v={form.vehicle_capacity_notes} on={set("vehicle_capacity_notes")} placeholder="Es. portata 800 kg" />
          </div>
        </div>

        {/* Phase 44.31.11 — Patente SPOSTATA in /driver/documents (è un documento personale).
            Qui restano solo scadenza/compagnia assicurazione veicolo. */}
        <div className="pt-4 border-t border-dapnow-line min-w-0" data-testid="vehicle-compliance-block">
          <p className="overline">Assicurazione veicolo</p>
          <p className="text-xs text-dapnow-muted mt-1">
            Compagnia + data di scadenza assicurazione del veicolo. Se scade, gli ordini si bloccano automaticamente fino all'aggiornamento. Ti avvisiamo 30 giorni prima.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-3 min-w-0">
            <Field
              label="Compagnia assicurativa"
              testid="vehicle-insurance-company"
              v={form.insurance_company}
              on={set("insurance_company")}
              placeholder="Es. Zurich, Allianz, AXA…"
            />
            <div className="min-w-0">
              <label className="text-sm font-semibold tracking-tight">Scadenza assicurazione</label>
              <input
                type="date"
                data-testid="vehicle-insurance-expiry"
                value={form.insurance_expiry}
                onChange={set("insurance_expiry")}
                className="input-tall mt-1.5 w-full"
              />
            </div>
          </div>
          <p className="text-[11px] text-dapnow-muted mt-3">
            Patente (numero + scadenza) si gestisce nella pagina{" "}
            <Link to="/driver/documents" className="text-dapnow-blue font-semibold hover:underline">
              Documenti
            </Link>.
          </p>
        </div>

        {info.verified_vehicle ? (
          <p className="text-sm text-emerald-700 inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Veicolo verificato</p>
        ) : (
          <p className="text-sm text-amber-700 inline-flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" /> In attesa di verifica documenti veicolo da parte dell'admin.</p>
        )}

        <div className="flex justify-end">
          <button onClick={save} disabled={busy} data-testid="vehicle-save-btn" className="btn-primary">
            {busy ? "Salvataggio…" : "Salva veicolo"}
          </button>
        </div>
      </section>
    </div>
  );
}

function Field({ label, v, on, testid, placeholder, error }) {
  return (
    <div>
      <label className="text-sm font-semibold tracking-tight">{label}</label>
      <input
        data-testid={testid}
        value={v}
        onChange={on}
        className={`input-tall mt-1.5 w-full ${error ? "border-dapnow-red focus:border-dapnow-red focus:ring-red-500/20" : ""}`}
        placeholder={placeholder}
      />
      {error && <p className="text-xs text-dapnow-red mt-1 font-medium" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}
